import React, { useState } from "react";
import axios from "axios";
import styles from "./EventsSearchPage.module.css";
import heroBg from "../assets/hero-crowd.jpg";
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

const EventsSearchPage = () => {
  const [results, setResults] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [keywordError, setKeywordError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const { t } = useTranslation('eventsSearch');

  const onSubmit = async (e) => {
    e.preventDefault();

    const q = keyword.trim();
    if (!q) {
      setKeywordError(t('error.required'));
      setHasSearched(false);
      return;
    }

    setKeywordError("");
    setHasSearched(true);
    setSearchError("");
    setIsSearching(true);

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/events`, {
        params: {
          q,
          upcoming: true,
          limit: 20,
        },
      });
      setResults(res.data.events || []);
    } catch (err) {
      setResults([]);
      const status = err?.response?.status;
      if (status === 422) {
        setSearchError(t('error.required'));
      } else {
        setSearchError(t('error.failed'));
      }
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className={styles.page}>
      <Helmet>
        <title>イベント検索 | IRIE TIX</title>
        <meta name="description" content="IRIE TIXでイベントを検索。Search for events and live music on IRIE TIX." />
      </Helmet>
      <section className={styles.hero}>
        <div
          className={styles.heroBg}
          style={{ backgroundImage: `url(${heroBg})` }}
          aria-hidden="true"
        />
        <div className={styles.heroOverlay} />

        <div className={styles.heroInner}>
          <h1 className={styles.heroTitle}>{t('title')}</h1>

          <form className={styles.searchShell} onSubmit={onSubmit}>
            <div className={styles.searchRow}>
              <div className={styles.field}>
                <input
                  className={styles.input}
                  value={keyword}
                  onChange={(e) => {
                    setKeyword(e.target.value);
                    if (keywordError) setKeywordError("");
                    if (searchError) setSearchError("");
                  }}
                  placeholder={t('placeholder')}
                  aria-label={t('ariaLabel')}
                />
              </div>

              <button type="submit" className={styles.searchButton} disabled={isSearching}>
                {isSearching ? t('searching') : t('search')}
              </button>
            </div>

            {keywordError && <div className={styles.searchError}>{keywordError}</div>}
            {searchError && <div className={styles.searchError}>{searchError}</div>}
          </form>
        </div>
      </section>

      <section className={styles.listSection}>
        <div className={styles.listHeader}>
          <h2 className={styles.listTitle}>{t('listTitle')}</h2>
          {hasSearched && <div className={styles.count}>{t('count', { count: results.length })}</div>}
        </div>

        {!hasSearched ? (
          <div className={styles.empty}>{t('empty')}</div>
        ) : isSearching ? (
          <div className={styles.empty}>{t('searching')}</div>
        ) : results.length === 0 ? (
          <div className={styles.empty}>{t('noResults')}</div>
        ) : (
          <div className={styles.list}>
            {results.map((event) => {
              const eventPathId = event?.slug || event?.id;
              const imgSrc = event?.flyers?.[0]?.url || null;
              const dateText = event?.date ? String(event.date).slice(0, 10) : "";
              const venueText = event?.venue ? `@ ${event.venue}` : "";

              return (
                <div key={event.id} className={styles.item}>
                  <div className={styles.thumbWrap}>
                    {imgSrc ? (
                      <img
                        src={imgSrc}
                        alt={`${event.name} flyer`}
                        className={styles.thumb}
                        loading="lazy"
                      />
                    ) : (
                      <div className={styles.noThumb}>{t('noImage')}</div>
                    )}
                  </div>

                  <div className={styles.itemBody}>
                    <div className={styles.itemName}>{event.name}</div>
                    <div className={styles.itemMeta}>{dateText}</div>
                    <div className={styles.itemMeta}>{venueText}</div>
                  </div>

                  <div className={styles.itemAction}>
                    <a href={`/events/${eventPathId}`} className={styles.detailLink}>
                      <button className={styles.detailButton}>{t('viewDetail')}</button>
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default EventsSearchPage;
